/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

var ToggleFsButton = class extends HTMLButtonElement {
  constructor() {
    super();
    if (this.hasAttribute("loader")) {
      this._prepareButton();
    }
    this.addEventListener("click", this._onButtonClick.bind(this));
    this.rootDelegate = new main_default(document.documentElement);
  }

  _onButtonClick() {
    document.querySelector('#fast-simon-serp-app .filters-button-wrapper')?.click();
  }

  disconnectedCallback() {
    this.rootDelegate.destroy();
  }
};

window.customElements.define("toggle-fs-filters", ToggleFsButton, { extends: "button" });

var ToggleFsSort = class extends HTMLButtonElement {
  constructor() {
    super();
    if (this.hasAttribute("loader")) {
      this._prepareButton();
    }

    this._setupAccessibility();

    this.addEventListener("click", this._onButtonClick.bind(this));
    this.rootDelegate = new main_default(document.documentElement);
  }

  _setupAccessibility() {
    setTimeout(() => {
      const fsCustomOptions = document.querySelector('.fs-custom-options');
      const fsSelectWrapper = document.querySelector('.fs-select-wrapper');

      if (fsCustomOptions) {
        fsCustomOptions.id = 'fs-sort-options-content';
      }

      if (fsCustomOptions?.id) {
        this.setAttribute('aria-controls', fsCustomOptions.id);
        fsSelectWrapper.setAttribute('aria-controls', fsCustomOptions.id);
      }

      this.setAttribute('aria-expanded', 'false');

      if (fsSelectWrapper) {
        fsSelectWrapper.addEventListener('click', () => {
          setTimeout(() => {
            const isOpen = fsSelectWrapper.classList.contains('fs-sort-options-open');
            fsSelectWrapper.setAttribute('aria-expanded', isOpen.toString());
          }, 100);
        });

        const isOpen = fsSelectWrapper.classList.contains('fs-sort-options-open');
        this.setAttribute('aria-expanded', isOpen.toString());
      }
    }, 2000);
  }

  _onButtonClick() {
    const fsSelect = document.querySelector('#fast-simon-serp-app .fs-select-wrapper .fs-select');
    fsSelect?.click();

    setTimeout(() => {
      const fsSelectWrapper = document.querySelector('.fs-select-wrapper');
      if (fsSelectWrapper) {
        const isOpen = fsSelectWrapper.classList.contains('fs-sort-options-open');
        this.setAttribute('aria-expanded', isOpen.toString());
      }
    }, 100);
  }

  disconnectedCallback() {
    this.rootDelegate.destroy();
  }
};

window.customElements.define("toggle-fs-sort", ToggleFsSort, { extends: "button" });


/* Fast Simon - Upsell & Cross-sell */
function hooks() {

  function checkGrid(entries) {
    entries.forEach((entry, index, arr) => {
      const totalSlides = arr.length;
      const slideNumber = index + 1;

      entry.target.setAttribute("role", "group");
      entry.target.setAttribute("aria-label", `slide ${slideNumber} of ${totalSlides}`);

      if (entry.isIntersecting) {
        entry.target.setAttribute("tabindex", "0"); // Make focusable if visible
      } else {
        entry.target.setAttribute("tabindex", "-1"); // Remove focus if not visible
      }
    });
  }

  function observeProducts(element) {
    const observer = new IntersectionObserver(checkGrid, {
      root: document.querySelector(".fs-upsell-cross-sell-wrapper") || null,
      rootMargin: "0px",
      threshold: 0.1,
    });

    const productElements = [...element.querySelectorAll(".fs-upsell-cross-sell-product")];
    productElements.forEach(product => observer.observe(product));
  }

  window.FastRecommendationWidget.registerHook("upsell-cross-sell-products", ({ element }) => {
    observeProducts(element);
    addCarouselAttributes(element);
  });

  // Recheck when next/prev buttons are clicked
  document.addEventListener("click", (event) => {
    if (event.target.closest(".fs-recommendation-button")) {
      const slides = document.querySelectorAll(".fs-upsell-cross-sell-product");
      slides.forEach((product, index) => {
        const totalSlides = slides.length;
        const slideNumber = index + 1;

        product.setAttribute("role", "group");
        product.setAttribute("aria-label", `slide ${slideNumber} of ${totalSlides}`);
        product.setAttribute("tabindex", "-1");
      });

      setTimeout(() => {
        slides.forEach(product => {
          if (product.getBoundingClientRect().right > 0 && product.getBoundingClientRect().left < window.innerWidth) {
            product.setAttribute("tabindex", "0");
          }
        });
      }, 300);
    }
  });

  // Add aria-label and role to the .widget-main element
  function addCarouselAttributes(element) {
    const carouselWrapper = element.closest('.widget-main'); // Find the parent element with .widget-main class
    if (carouselWrapper) {
      let carouselTitle = carouselWrapper.querySelector('.fs-recommendation-widget-title');
      carouselWrapper.setAttribute("role", "region");
      carouselWrapper.setAttribute("aria-label", `${carouselTitle.textContent} carousel`);
    }
  }

}

if (window.FastRecommendationWidget) {
  hooks();
} else {
  window.addEventListener("fast-upsell-cross-sell-ready", hooks);
}



// js/custom-element/behavior/toggle-button.js: Accessible toggle button
var ToggleButton = class extends HTMLButtonElement {
  static get observedAttributes() {
    return ["data-aria-expanded", "data-aria-busy"];
  }
  constructor() {
    super();
    if (this.hasAttribute("loader")) {
      this._prepareButton();
    }
    this.addEventListener("click", this._onButtonClick.bind(this));
    this.rootDelegate = new main_default(document.documentElement);
  }
  _onButtonClick() {
    this.isExpanded = !this.isExpanded;
  }
  connectedCallback() {
    document.addEventListener("openable-element:close", (event) => {
      if (this.controlledElement === event.target) {
        this.isExpanded = false;
        event.stopPropagation();
      }
    });
    document.addEventListener("openable-element:open", (event) => {
      if (this.controlledElement === event.target) {
        this.isExpanded = true;
        event.stopPropagation();
      }
    });
    this.rootDelegate.on("openable-element:load:start", `#${this.getAttribute("aria-controls")}`, () => {
      if (this.classList.contains("button")) {
        this.setAttribute("data-aria-busy", "true");
      } else if (this.offsetParent !== null) {
        triggerEvent(document.documentElement, "theme:loading:start");
      }
    }, true);
    this.rootDelegate.on("openable-element:load:end", `#${this.getAttribute("aria-controls")}`, () => {
      if (this.classList.contains("button")) {
        this.removeAttribute("data-aria-busy");
      } else if (this.offsetParent !== null) {
        triggerEvent(document.documentElement, "theme:loading:end");
      }
    }, true);
  }
  disconnectedCallback() {
    this.rootDelegate.destroy();
  }
  get isExpanded() {
    return this.getAttribute("data-aria-expanded") === "true";
  }
  set isExpanded(value) {
    this.setAttribute("data-aria-expanded", value ? "true" : "false");
  }
  get controlledElement() {
    return document.getElementById(this.getAttribute("data-aria-controls"));
  }
  attributeChangedCallback(name, oldValue, newValue) {
    switch (name) {
      case "data-aria-expanded":
        if (oldValue === "false" && newValue === "true") {
          this.controlledElement.open = true;
        } else if (oldValue === "true" && newValue === "false") {
          this.controlledElement.open = false;
        }
        break;
      case "data-aria-busy":
        if (this.hasAttribute("loader")) {
          if (newValue === "true") {
            this._startTransition();
          } else {
            this._endTransition();
          }
        }
        break;
    }
  }
};
Object.assign(ToggleButton.prototype, LoaderButtonMixin);
window.customElements.define("cs-toggle-button", ToggleButton, { extends: "button" });


// js/custom-element/behavior/toggle-link.js: Accessible toggle link
var ToggleLink = class extends HTMLAnchorElement {
  static get observedAttributes() {
    return ["data-aria-expanded"];
  }
  constructor() {
    super();
    this.addEventListener("click", (event) => {
      event.preventDefault();
      this.isExpanded = !this.isExpanded;
    });
    this.rootDelegate = new main_default(document.documentElement);
  }
  connectedCallback() {
    this.rootDelegate.on("openable-element:close", `#${this.getAttribute("data-aria-controls")}`, (event) => {
      if (this.controlledElement === event.target) {
        this.isExpanded = false;
      }
    }, true);
    this.rootDelegate.on("openable-element:open", `#${this.getAttribute("data-aria-controls")}`, (event) => {
      if (this.controlledElement === event.target) {
        this.isExpanded = true;
      }
    }, true);
  }
  disconnectedCallback() {
    this.rootDelegate.destroy();
  }
  get isExpanded() {
    return this.getAttribute("data-aria-expanded") === "true";
  }
  set isExpanded(value) {
    this.setAttribute("data-aria-expanded", value ? "true" : "false");
  }
  get controlledElement() {
    return document.querySelector(`#${this.getAttribute("data-aria-controls")}`);
  }
  attributeChangedCallback(name, oldValue, newValue) {
    switch (name) {
      case "data-aria-expanded":
        if (oldValue === "false" && newValue === "true") {
          this.controlledElement.open = true;
        } else if (oldValue === "true" && newValue === "false") {
          this.controlledElement.open = false;
        }
    }
  }
};
window.customElements.define("cs-toggle-link", ToggleLink, { extends: "a" });

// Accessibility Fix: Live announcement of search results
function hooks() {
  const searchHeader = document.querySelector("#search-header");

  // Add ARIA live region to announce the number of results found
  const resultsCount = document.createElement("div");
  resultsCount.classList.add("results-count", "sr-only");
  resultsCount.setAttribute("aria-live", "assertive");
  searchHeader.after(resultsCount);


  // Add instructions for screen reader users
  const instructions = document.createElement("div");
  instructions.id = "instructions";
  instructions.style.display = "none";
  instructions.textContent = "Begin typing to search, use arrow keys to navigate, Enter to select";
  searchHeader.after(instructions);


  // On keyup searchHeader show 0 results found
  searchHeader.addEventListener("keyup", function () {
    resultsCount.innerHTML = "0 results found.";
  });

  FastAutocomplete.registerHook('render-autocomplete-product-item', ({ index }) => {

    // Remove any previous content of the ARIA live region so that any new text added is announced, even if that new text happens to match the previous text.
    resultsCount.innerHTML = '';

    // Announce the number of results found to screen reader users using the ARIA live region.
    if (index > 1) {
      resultsCount.innerHTML = index + ' results found.';
    } else if (index === 1) {
      resultsCount.innerHTML = 'One result found.';
    } else {
      resultsCount.innerHTML = 'No results found.';
    }

  });
}

if (window.FastAutocomplete) {
  hooks();
} else {
  window.addEventListener('fast-autocomplete-ready', function () {
    hooks();
  });
}


//ADA Issues
function setupDialogFocusTrap(dialog) {
  // Get all focusable elements
  const focusableElements = dialog.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );

  const firstFocusableElement = focusableElements[0];
  const lastFocusableElement = focusableElements[focusableElements.length - 1];

  function handleTabKey(e) {
    if (e.key === 'Tab') {
      if (e.shiftKey && document.activeElement === firstFocusableElement) {
        e.preventDefault();
        lastFocusableElement.focus();
      } else if (!e.shiftKey && document.activeElement === lastFocusableElement) {
        e.preventDefault();
        firstFocusableElement.focus();
      }
    }
  }
  dialog.addEventListener('keydown', handleTabKey);
  return () => dialog.removeEventListener('keydown', handleTabKey);
}

document.addEventListener('DOMContentLoaded', function() {
  const cartDrawer = document.querySelector('[data-open-cart-drawer]');
  cartDrawer.addEventListener('keydown', function(e) {
    const target = e.target;
    if (e.key === 'Enter' && target.classList.contains('header__icon-wrapper')) {
      setupDialogFocusTrap(cartDrawer);
    }
  });
});

function fixPaginationStructure() {
  const paginationWrapper = document.querySelector('.pagination-wrapper');

  if (!paginationWrapper || paginationWrapper.tagName === 'NAV') {
    return;
  }

  const navElement = document.createElement('nav');
  navElement.setAttribute('aria-label', 'pagination');

  Array.from(paginationWrapper.attributes).forEach(attr => {
    if (attr.name !== 'bis_skin_checked') {
      navElement.setAttribute(attr.name, attr.value);
    }
  });

  while (paginationWrapper.firstChild) {
    navElement.appendChild(paginationWrapper.firstChild);
  }

  paginationWrapper.parentNode.replaceChild(navElement, paginationWrapper);
}

function fixFastSimonPaginationAccessibility() {
  const selectedPaginationLink = document.querySelector('.page-number-item-selected');

  if (selectedPaginationLink && !selectedPaginationLink.hasAttribute('aria-current')) {
    selectedPaginationLink.setAttribute('aria-current', 'page');
  }
}

function setupPaginationAccessibility() {
  fixFastSimonPaginationAccessibility();
}

function checkForPaginationAndFix() {
  if (document.querySelector('.pagination-wrapper')) {
    setupPaginationAccessibility();

    if (!window.paginationIntervalSet) {
      setInterval(setupPaginationAccessibility, 2000);
      window.paginationIntervalSet = true;
    }
  } else {
    setTimeout(checkForPaginationAndFix, 500);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function() {
    checkForPaginationAndFix();
  });
} else {
  checkForPaginationAndFix();
}


document.addEventListener('DOMContentLoaded', function() {
  const categoryCheckInterval = setInterval(function() {
    const categoryItems = document.querySelector('.category-items');

    if (categoryItems) {
      const srOnlySpan = document.createElement('span');
      srOnlySpan.className = 'sr-only';
      srOnlySpan.textContent = 'Activating this elements will cause content on the page to be updated';

      categoryItems.insertBefore(srOnlySpan, categoryItems.firstChild);

      clearInterval(categoryCheckInterval);
    }
  }, 200);


  setTimeout(function() {
    clearInterval(categoryCheckInterval);
  }, 5000);
});


// Accessibility enhancement for price range slider inputs
document.addEventListener('DOMContentLoaded', function() {
  // Always set up the interval to check for price range slider inputs
  const priceRangeCheckInterval = setInterval(function() {
    const minPriceInput = document.getElementById('price-range-slider-min');
    const maxPriceInput = document.getElementById('price-range-slider-max');

    // Check if both inputs exist
    if (minPriceInput && maxPriceInput) {
      // Create and add label for min price input
      if (!document.querySelector('label[for="price-range-slider-min"]')) {
        const minLabel = document.createElement('label');
        minLabel.setAttribute('for', 'price-range-slider-min');
        minLabel.className = 'sr-only'; // Screen reader only class
        minLabel.textContent = 'selected starting price';
        minPriceInput.parentNode.insertBefore(minLabel, minPriceInput);
      }
      
      // Create and add label for max price input
      if (!document.querySelector('label[for="price-range-slider-max"]')) {
        const maxLabel = document.createElement('label');
        maxLabel.setAttribute('for', 'price-range-slider-max');
        maxLabel.className = 'sr-only'; // Screen reader only class
        maxLabel.textContent = 'selected ending price';
        maxPriceInput.parentNode.insertBefore(maxLabel, maxPriceInput);
      }

      clearInterval(priceRangeCheckInterval);
    }
  }, 200);

  // Stop checking after 5 seconds total
  setTimeout(function() {
    clearInterval(priceRangeCheckInterval);
  }, 5000);
});

// Fix color swatch accessibility
function fixColorSwatchAccessibility() {
  const colorSwatchContainers = document.querySelectorAll('.fs-color-swatch-container');
  if (!colorSwatchContainers.length) return;

  const swatchesParent = document.querySelector('.swatches');
  if (swatchesParent) {
    if (!swatchesParent.hasAttribute('role')) {
      swatchesParent.setAttribute('role', 'radiogroup');

      if (!document.getElementById('color-legend')) {
        const legendHeading = document.createElement('h2');
        legendHeading.id = 'color-legend';
        legendHeading.className = 'legend sr-only';
        legendHeading.textContent = 'Choose a color';

        swatchesParent.insertBefore(legendHeading, swatchesParent.firstChild);
      }

      swatchesParent.setAttribute('aria-labelledby', 'color-legend');
    }
  }

  colorSwatchContainers.forEach(swatch => {
    if (swatch.getAttribute('role') === 'radio') return;

    const colorNameElement = swatch.querySelector('.fs-color-name');
    const colorName = colorNameElement ? colorNameElement.textContent.trim() : '';
    const colorTitle = swatch.querySelector('.color-swatch')?.getAttribute('title') || colorName;

    const isSelected = swatch.classList.contains('selected') || swatch.getAttribute('aria-pressed') === 'true';

    swatch.setAttribute('role', 'radio');
    swatch.setAttribute('aria-checked', isSelected ? 'true' : 'false');
    swatch.setAttribute('tabindex', isSelected ? '0' : '-1');

    if (!swatch.querySelector('.sr-only')) {
      const srOnlySpan = document.createElement('span');
      srOnlySpan.className = 'sr-only';
      srOnlySpan.textContent = colorTitle;
      swatch.appendChild(srOnlySpan);
    }

    swatch.addEventListener('keydown', (e) => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        swatch.click();
      }
    });

    swatch.addEventListener('click', () => {
      setTimeout(() => {
        const allSwatches = document.querySelectorAll('.swatches [role="radio"]');
        allSwatches.forEach(s => {
          const isNowSelected = s.classList.contains('selected') || s.getAttribute('aria-pressed') === 'true';
          s.setAttribute('aria-checked', isNowSelected ? 'true' : 'false');
          s.setAttribute('tabindex', isNowSelected ? '0' : '-1');
        });
      }, 100);
    });
  });
}

function transformResultsTextToHeading() {
  const resultsTextDiv = document.querySelector('.fs-total-results-text');
  if (!resultsTextDiv || resultsTextDiv.tagName === 'H2') return;

  const h2Element = document.createElement('h2');

  Array.from(resultsTextDiv.attributes).forEach(attr => {
    h2Element.setAttribute(attr.name, attr.value);
  });

  h2Element.innerHTML = resultsTextDiv.innerHTML;

  resultsTextDiv.parentNode.replaceChild(h2Element, resultsTextDiv);
}

document.addEventListener('DOMContentLoaded', function() {
  const colorSwatchInterval = setInterval(function() {
    if (document.querySelector('.fs-color-swatch-container')) {
      fixColorSwatchAccessibility();
    }

    if (document.querySelector('.fs-total-results-text')) {
      transformResultsTextToHeading();
    }
  }, 200);


  setTimeout(function() {
    clearInterval(colorSwatchInterval);
  }, 4000);
});

// Transform storepoint-name divs into h3 elements
function transformStorepointNames() {
  const storepointCheckInterval = setInterval(function() {
    const storepointNames = document.querySelectorAll('.storepoint-name');
    if (storepointNames.length > 0) {
      storepointNames.forEach(div => {
        const h3 = document.createElement('h3');
        Array.from(div.attributes).forEach(attr => {
          h3.setAttribute(attr.name, attr.value);
        });

        while (div.firstChild) {
          h3.appendChild(div.firstChild);
        }

        h3.classList.add('mb-0');
        div.parentNode.replaceChild(h3, div);
      });

      clearInterval(storepointCheckInterval);
    }
  }, 400);

  setTimeout(function() {
    clearInterval(storepointCheckInterval);
  }, 4000);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', transformStorepointNames);
} else {
  transformStorepointNames();
}




